import { ProductDetails } from '../AddProductToSale/useAddProductToSale'
import {
  ApiLayoutData,
  ApiNode,
  AttributeKey,
  QuickKey,
  QuickKeyBlank,
  QuickKeyFolder,
  QuickKeyKey,
  QuickKeyRoot,
} from './utils/qk-types'
import { QuickKeyNodeType } from './utils/qk.constants'

export type DragStatus = 'invalid_target' | 'valid_target'

export interface HandleDropParams {
  dropped: QuickKeyKey | QuickKeyFolder
  target: QuickKey
}

export interface AddProductParams {
  product: ProductDetails
  node: ApiNode
  currentRoot: ApiNode | null
  isChild: boolean
}

export const getRootNodeForLayout = (data: ApiLayoutData): ApiNode | null => {
  const rootNode = data.nodes.find(node => node.parent_node_id === null)
  return rootNode || null
}

export const getNodeById = (
  data: ApiLayoutData,
  nodeId: string
): ApiNode | null => {
  const node = data.nodes.find(n => n.id === nodeId)
  return node || null
}

export const getNodeAttribute = (node: ApiNode, key: string) => {
  const attribute = node.attributes.find(attr => attr.key === key)
  return attribute ? attribute.value : null
}

export const setNodeAttribute = (
  node: ApiNode,
  key: AttributeKey,
  value: string
): ApiNode => {
  let hasFound = false
  const attributes: Array<{ key: AttributeKey; value: string }> = []
  node.attributes.forEach(attr => {
    if (attr.key === key) {
      hasFound = true
      attributes.push({ key, value })
    } else {
      attributes.push(attr)
    }
  })

  if (!hasFound) {
    attributes.push({ key, value })
  }

  return { ...node, attributes }
}

export const updateNode = (
  data: ApiLayoutData,
  nodeId: string,
  updatedNode: ApiNode
): ApiLayoutData => {
  const nodes = data.nodes.map(node =>
    node.id === nodeId ? updatedNode : node
  )
  return { ...data, nodes }
}

export const getChildrenForNode = (
  data: ApiLayoutData,
  nodeId: string | null
): ApiNode[] => {
  return data.nodes.filter(node => node.parent_node_id === nodeId)
}

export const getFirstChild = (
  node: QuickKey
): QuickKeyKey | QuickKeyFolder | null => {
  if (!('children' in node && node.children.length)) {
    return null
  }

  const firstChild = node.children[0]
  if (
    firstChild.type === QuickKeyNodeType.FOLDER ||
    firstChild.type === QuickKeyNodeType.QUICK_KEY
  ) {
    return firstChild
  }

  return getFirstChild(firstChild)
}

export const getFirstChildForNode = (
  layoutData: ApiLayoutData,
  node: ApiNode
): ApiNode | null => {
  let result: ApiNode | null = null

  layoutData.nodes.forEach(layoutNode => {
    if (
      layoutNode.parent_node_id === node.id &&
      (!result || layoutNode.position < result.position)
    ) {
      result = layoutNode
    }
  })
  return result
}

/**
 * Find the first blank in a QuickKey layout.
 */
export const getFirstBlank = (
  layout: QuickKeyRoot | QuickKeyFolder
): QuickKeyBlank | null => {
  const pages = layout.children
  let blank: QuickKeyBlank | null = null
  let blankPageIndex: number | null = null

  pages.forEach((page, pageIndex) => {
    page.viewData.nodes.forEach(child => {
      if (child.type !== QuickKeyNodeType.BLANK) {
        return
      }
      if (blank === null) {
        blank = child
        blankPageIndex = pageIndex
        return
      }
      if (
        pageIndex === blankPageIndex &&
        child.node.position < blank.node.position
      ) {
        blank = child
        return
      }
      if (pageIndex < Number(blankPageIndex)) {
        blank = child
        blankPageIndex = pageIndex
        return
      }
    })
  })
  return blank
}

export const getFirstBlankPositionForPage = (
  layoutData: ApiLayoutData,
  node: ApiNode
): number | null => {
  const childPositions = layoutData.nodes
    .filter(n => n.parent_node_id === node.id)
    .map(n => n.position)
  const rows = Number(getNodeAttribute(node, 'rows') || 0)
  const columns = Number(getNodeAttribute(node, 'columns') || 0)
  const length = rows * columns

  let firstBlank: number | null = null

  for (let i = 0; i < length; i++) {
    if (!childPositions.includes(i)) {
      firstBlank = i
      break
    }
  }

  return firstBlank
}

/**
 * Responsbile for determining if the provided quick key is a valid droppable destination for the currently
 * dragged quick key.
 */
export const isValidDropDestination = (
  quickKey: QuickKey,
  dragged: QuickKey
) => {
  if (quickKey.locked) {
    return false
  }

  const { FOLDER, QUICK_KEY, BLANK } = QuickKeyNodeType
  const draggedQkIsFolder = dragged.type === FOLDER

  switch (quickKey.type) {
    case FOLDER:
      // Only allow dragging into a folder if we're not dragging another folder
      return !draggedQkIsFolder

    case QUICK_KEY: {
      const isInFolder = Boolean(quickKey.parentNode.parent_node_id)
      // Only allow dragging onto another quick key if we're not dragging a folder and not inside a folder
      return !(draggedQkIsFolder || isInFolder)
    }

    case BLANK:
      return true

    default:
      return false
  }
}

export const getClassesForStatus = (status?: DragStatus | null) => {
  switch (status) {
    case 'valid_target':
      return ['vd-quick-key--drag-over']
    case 'invalid_target':
      return [
        'vd-quick-key--drag-over',
        'vd-quick-key--drag-over vd-quick-key--not-allowed',
      ]
    default:
      return []
  }
}
